import { DetailModalLink } from '@/components/ui/detailModalLink';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { FieldValue } from '@pigello/pigello-matrix';

import { cn } from './cn';
import type { Entries, Values } from './types';
import { Urls } from './urls';

export function raise(err: string): never {
  throw new Error(err);
}

export function filterDuplicates<T extends Array<unknown>>(
  array: T,
  accessorFn?: (item: T[number]) => unknown
): T {
  const accessor = accessorFn ?? ((item) => item);

  const seenKeys = new Set<unknown>();
  const result = [];
  for (const item of array) {
    const key = accessor(item);
    if (!seenKeys.has(key)) {
      seenKeys.add(key);
      result.push(item);
    }
  }
  return result as T;
}

export const ObjectEntries = <T extends object>(obj: T) =>
  Object.entries(obj) as Entries<T>;

export const ObjectValues = <T extends object>(obj: T) =>
  Object.values(obj) as Values<T>;

export const ObjectKeys = <T extends object>(obj: T) =>
  Object.keys(obj) as (keyof T)[];

/**
 * @description
 * 
 * @param data - The data to check.
 * @param key - The key to check for.
 * @returns True if the key exists in the data and the value is not null or undefined. False otherwise 
 * @example
 * ```tsx
 * type Data = { name: string; age: number };
 * const data = { name: 'John', age: 42 };
 * const name = typeGuard<Data>(data, 'name') ? data.name : 'No name';
 * console.log(name); // 'John'
 * ```
 * @example
 * ```tsx
 *   const corporateName = typeGuard<Tenant>(data, 'corporateName')
    ? data.corporateName
    : false;

  if (corporateName) {
    return corporateName;
  }...
 * ```
 */
export function typeGuard<T>(data: unknown, key: keyof T): data is T {
  return (
    typeof data === 'object' &&
    data != null &&
    key in data &&
    data?.[key as keyof typeof data] != null
  );
}

export function isDev() {
  return true;
}

export const camelToSnake = (str: string) => {
  return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
};

export function toSqm(value: string | number | undefined | null) {
  if (value == null) return '-';

  if (typeof value === 'string' && value.length === 0) return '-';

  // TODO: Make sure this change is alright
  return `${value.toLocaleString('sv', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}m²`;
}

export function toWeekInterval(weeks: FieldValue<string>) {
  if (!weeks) return '-';
  const intWeeks = parseInt(weeks);
  if (intWeeks <= 3) {
    return intWeeks === 1 ? intWeeks + ' vecka' : `${intWeeks} veckor`;
  }

  return intWeeks / 4 === 1
    ? intWeeks / 4 + ' månad'
    : intWeeks / 4 + ' månader';
}

type ReadableAddressInput = {
  id?: string;
  street?: string | null;
  streetNumber?: string | null;
  postalCode?: string | null;
  city?: string | null;
  careOf?: string | null;
  attention?: string | null;
};

export function toReadableAddress(
  address?: ReadableAddressInput | null,
  long = true,
  withDetailLink = true,
  className?: string
) {
  if (!address) return null;

  if (!address.street) return '-';
  const careOf = address.careOf ? `C/O: ${address.careOf}` : '';
  const attention = address.attention ? `Attn: ${address.attention}` : '';

  const readable = `${address.street ?? ''} ${address.streetNumber ?? ''}${
    long
      ? `, ${address.postalCode ?? ''}, ${address.city ?? ''} ${careOf} ${attention}`
      : ''
  }`;

  if (withDetailLink) {
    return (
      <Tooltip>
        <TooltipContent>{readable}</TooltipContent>
        <TooltipTrigger asChild>
          <DetailModalLink
            className={cn('inline-block max-w-48 truncate', className)}
            pathname={Urls.detail.address.overview(address.id)}
          >
            {readable}
          </DetailModalLink>
        </TooltipTrigger>
      </Tooltip>
    );
  }

  return readable;
}

export const DELIVERY_METHOD_CODES = {
  SMS: 0,
  EMAIL: 1,
  DIGITAL_INBOX: 2,
  MAIL: 3,
  E_INVOICE_BANK: 4,
  E_INVOICE_PEPPOL_SVEFAKTURA: 5,
} as const;

export const DELIVERY_METHOD_MAP: Record<number, string> = {
  0: 'SMS',
  1: 'E-post',
  2: 'Digital brevlåda',
  3: 'Post',
  4: 'E-faktura (bank)',
  5: 'E-faktura (PEPPOL/Svefaktura)',
};

export function toReadableDistributionMethod(
  distributionArray: number[][] | null
) {
  if (!distributionArray || distributionArray.length === 0) return '-';

  const primary = distributionArray[0];

  return primary.map((method) => DELIVERY_METHOD_MAP[method]).join(', ');
}

export function toReadableDayInMonth(day: string | number | undefined) {
  if (!day) {
    return '-';
  }
  const val = day.toString();

  const lastChar = val[val.length - 1];

  if (['1', '2'].includes(lastChar)) return `${val}:a`;

  return `${val}:e`;
}

export function toReadableDayInterval(day?: string | number | null) {
  if (!day) return '-';
  const val = day.toString();

  if (val === '1') return 'varje';
  if (val === '2') return 'varannan';

  return `var ${val}:e`;
}

export function toMoneyString(value: FieldValue<number>) {
  if (value == null) return '-';

  return `${value.toLocaleString('sv', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} kr`;
}

export function toPercentageString(
  value?: FieldValue<number> | string | number
) {
  if (value == null) return '-';
  return `${value}%`;
}

export function toMonthString(value: FieldValue<number>) {
  if (value == null) return '-';
  if (value === 1) return `${value} månad`;
  return `${value} månader`;
}

export function toDayString(value: FieldValue<number>) {
  if (value == null) return '-';
  if (value === 1) return `${value} dag`;
  return `${value} dagar`;
}

export function toAmountString(value: FieldValue<number>) {
  if (value == null) return '-';
  return `${value} st`;
}

/**
 * Rounds a number to the nearest integer.
 *
 * For values with a fractional part of exactly `0.5`, it applies
 * "bankers rounding" (round half to even) by returning the nearest even integer.
 * For all other values, it uses `Math.round`.
 *
 * @param amount - The numeric amount to round.
 * @returns The rounded integer value.
 */
export function toRoundedInteger(amount: number) {
  if (Math.abs(amount % 1) === 0.5) {
    return Math.floor(amount) % 2 === 0
      ? Math.floor(amount)
      : Math.ceil(amount);
  }

  return Math.round(amount);
}

/**
 * Rounds a number to a fixed number of decimal places.
 *
 * Uses `Number.prototype.toFixed` and converts the resulting string back to a number.
 *
 * @param amount - The numeric amount to round.
 * @param decimals - Number of decimal places to keep (defaults to `2`).
 * @returns The amount rounded to the specified number of decimal places.
 */
export function toRoundedFloat(amount: number, decimals = 2) {
  return Number(amount.toFixed(decimals));
}

export const rentSqm = (rent: number, sqm: FieldValue<number>) => {
  if (!sqm) return '-';
  return `${toMoneyString(rent / sqm)}`;
};
